import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Components
import Layout from './components/Layout';
import ProtectedAdminRoute from './components/ProtectedAdminRouteProps';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import NewsPage from './pages/NewsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePage from './pages/CookiePage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import SteamCallbackPage from './pages/SteamCallbackPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminArticlesPage from './pages/admin/AdminArticlesPage';
import AdminArticleEditorPage from './pages/admin/AdminArticleEditorPage';
import AdminTeamPage from './pages/admin/AdminTeamPage';
import AdminCreateTeamPage from './pages/admin/AdminCreateTeampage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserEditorPage from './pages/admin/AdminUserEditorPage';
import MatchPage from './pages/MatchPage';
import MatchPageOverview from './components/MatchPageOverview';
import VetoPage from './pages/admin/VetoPage';
import ServerManagement from './pages/admin/ServerManagement';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';

// Context
import { EventProvider } from './context/EventContext';
import { AuthProvider } from './context/AuthContext';
import { NewsProvider } from './context/NewsContext';
import { MappoolProvider } from './context/MappoolContext';

// Styles
import './styles/global.scss';
import TeamDetailPage from './pages/TeamDetailPage';
import TeamsPage from './pages/TeamsPage';
import AdminMatchEditorPage from './pages/admin/AdminMatchEditorPage';
import AdminTournamentsPage from './pages/admin/AdminTournamentsPage';
import ViewMatchDetailsPage from './pages/admin/ViewMatchDetailsPage';
import MatchesPage from './pages/MatchesPage';
import InfoPage from './pages/InfoPage';
import LeaderboardPage from './pages/LeaderboardPage';
import { LiveMatchWebhookPage } from './pages/LiveMatchWebhookPage';
import FormatPitchesPage from './pages/FormatPitchesPage';


const App: React.FC = () => {
  return (
    <EventProvider>
      <NewsProvider>
        <BrowserRouter>
          <AuthProvider>
            <MappoolProvider>
              <Layout>
                <Routes>
                  {/* Regular routes */}
                  <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/teams/:id" element={<TeamDetailPage />} />
                <Route path="/steam/callback" element={<SteamCallbackPage />} />
                <Route path="/profile/:steamId" element={<ProfilePage />} />
                <Route path="/profile/:steamId/edit" element={<EditProfilePage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:id" element={<ArticleDetailPage />} />
                <Route path="/matches" element={<MatchesPage />} />
                <Route path="/matches/:matchId" element={<MatchPage />} />
                <Route path="/match/:matchId" element={<MatchPage />} />
                <Route path="/matches/:matchId/live-webhook" element={<LiveMatchWebhookPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                
                {/* Info pages */}
                <Route path="/info/rules" element={<InfoPage slug="rules" />} />
                <Route path="/info/prizepool" element={<InfoPage slug="prizepool" />} />
                <Route path="/info/format" element={<InfoPage slug="format" />} />
                <Route path="/info/schedule" element={<InfoPage slug="schedule" />} />
                <Route path="/info/faq" element={<InfoPage slug="faq" />} />
                <Route path="/info/format-pitches" element={<FormatPitchesPage />} />
                
                {/* Legal pages */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/cookies" element={<CookiePage />} />
                
                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route 
                  path="/admin/dashboard" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminDashboard />
                    </ProtectedAdminRoute>
                  } 
                />
                
                {/* Admin article management routes */}
                <Route 
                  path="/admin/articles" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminArticlesPage />
                    </ProtectedAdminRoute>
                  } 
                />
                <Route 
                  path="/admin/articles/new" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminArticleEditorPage />
                    </ProtectedAdminRoute>
                  } 
                />
                <Route 
                  path="/admin/articles/edit/:id" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminArticleEditorPage />
                    </ProtectedAdminRoute>
                  } 
                />

                {/* Admin user management routes */}
                <Route 
                  path="/admin/users" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminUsersPage />
                    </ProtectedAdminRoute>
                  } 
                />
                <Route 
                  path="/admin/users/create" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminUserEditorPage />
                    </ProtectedAdminRoute>
                  } 
                />
                <Route 
                  path="/admin/users/edit/:steamId" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminUserEditorPage />
                    </ProtectedAdminRoute>
                  } 
                />

                {/* Admin team management routes */}
                <Route 
                  path="/admin/teams" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminTeamPage />
                    </ProtectedAdminRoute>
                  } 
                />
                <Route 
                  path="/admin/teams/create" 
                  element={
                    <ProtectedAdminRoute>
                      <AdminCreateTeamPage />
                    </ProtectedAdminRoute>
                  } 
                />

                {/* Admin tournament routes - placeholders */}
                <Route path="/admin/tournaments" element={<AdminTournamentsPage />} />
                <Route path="/admin/tournaments/create" element={<AdminMatchEditorPage />} />
                <Route path="/admin/tournaments/edit/:challongeId" element={<AdminMatchEditorPage />} />
                <Route path="/admin/tournaments/view/:matchId" element={<ViewMatchDetailsPage />} />

                {/* Admin settings route */}
                <Route
                  path="/admin/settings"
                  element={
                    <ProtectedAdminRoute>
                      <AdminSettingsPage />
                    </ProtectedAdminRoute>
                  }
                />

                {/* Admin server management route */}
                <Route 
                  path="/admin/servers" 
                  element={
                    <ProtectedAdminRoute>
                      <ServerManagement />
                    </ProtectedAdminRoute>
                  } 
                />
                {/* Veto route - protected for admins only */}
                <Route 
                  path="/match/:matchId/veto" 
                  element={
                    <ProtectedAdminRoute>
                      <VetoPage />
                    </ProtectedAdminRoute>
                  } 
                />
              </Routes>
            </Layout>
            </MappoolProvider>
          </AuthProvider>
        </BrowserRouter>
      </NewsProvider>
    </EventProvider>
  );
};

export default App;